import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { GENERATION_STEPS, Job, jobMicroUsd } from '@quizdb/common';
import { formatMicroUsd } from './formatCost';

/** Props for `JobCostCard`. */
export interface JobCostCardProps {
  job: Job;
}

/** `tokensIn` and `tokensOut`, compact-formatted and slash-joined, e.g. "412K / 61K" (QZ-D55). */
const formatTokens = (tokensIn: number, tokensOut: number, locale: string): string => {
  const format = new Intl.NumberFormat(locale, { notation: 'compact' });
  return `${format.format(tokensIn)} / ${format.format(tokensOut)}`;
};

/** A job's cost, broken down by generation step, with its token totals (QZ-D53, QZ-D55). */
export const JobCostCard = ({ job }: JobCostCardProps) => {
  const { t, i18n } = useTranslation();

  const totalInputTokens = GENERATION_STEPS.reduce(
    (total, step) => total + job.cost[step].inputTokens,
    0
  );
  const totalOutputTokens = GENERATION_STEPS.reduce(
    (total, step) => total + job.cost[step].outputTokens,
    0
  );

  return (
    <div className="qz-card">
      <div className="qz-card-header">
        <h3>{t('job.page.cost')}</h3>
        <span className="qz-mono fw-semibold">{formatMicroUsd(jobMicroUsd(job))}</span>
      </div>
      <div className="qz-card-body">
        <dl className="qz-kv">
          {GENERATION_STEPS.map((step) => {
            const cost = job.cost[step];
            return (
              <Fragment key={step}>
                <dt>
                  {t(`job.step.${step}`)}
                  {cost.model && <div className="qz-mono qz-small qz-muted">{cost.model}</div>}
                </dt>
                <dd>
                  {formatMicroUsd(cost.microUsd)}
                  <div className="qz-small qz-muted">
                    {formatTokens(cost.inputTokens, cost.outputTokens, i18n.language)}
                  </div>
                </dd>
              </Fragment>
            );
          })}
          <dt>{t('job.field.tokens')}</dt>
          <dd>{formatTokens(totalInputTokens, totalOutputTokens, i18n.language)}</dd>
        </dl>
      </div>
    </div>
  );
};
